import React, { useState, useEffect, useRef } from 'react'
import { T } from '@tolgee/react'

import { PageTemplate } from '@/app/_common/page-template'
import { Button } from '@/components/ui/button'
import { DatabaseIcon } from '@/components/icons/database-icon'
import { ChartWithNoAxesIcon } from '@/components/icons/chart-with-no-axes-icon'
import { DataQualityView } from './_components/data-quality'
import { MetadataView } from './_components/metadata'
import { Select, SelectTrigger, SelectContent, SelectItem } from '@/components/ui/select'
import { TabDataGovernanceDashboard } from '@/app/_common/split-view/tabs/types'
import { useListDataAssetsQuery } from '@/hooks/api/use-datagovernance-dashboard-query'
import { Database } from './types'
import { Skeleton } from '@/components/ui/skeleton'
import { Breadcrumb } from '@/components/ui/breadcrumb'
import {
  useDataCatalogBreadcrumbs,
  useDataCatalogNavigation,
} from '../_common/hooks/use-data-catalog-breadcrumbs'
import { useSplitView } from '@/app/_common/split-view/split-view-context'

type FilterKey = 'database' | 'schema' | 'table' | 'sensitivity'

export type FiltersType = Record<FilterKey, string>

const resetMap: Record<FilterKey, FilterKey[]> = {
  database: ['schema', 'table', 'sensitivity'],
  schema: ['table', 'sensitivity'],
  table: ['sensitivity'],
  sensitivity: [],
}

export function DataGovernanceDashboard({ tab }: { tab: TabDataGovernanceDashboard }) {
  const { databaseName, schemaName, tableName, assetType, sensitivity } = tab || {}
  const { updateTab } = useSplitView()
  const isInitialMount = useRef(true)

  // Initialize state from tab props (URL)
  const [view, setView] = useState<'metaData' | 'dataQuality'>(tab.tabView)

  const [filters, setFilters] = useState<FiltersType>({
    database: databaseName || 'all',
    schema: schemaName || 'all',
    table: tableName || 'all',
    sensitivity: sensitivity || 'all',
  })
  const { breadcrumbItems } = useDataCatalogBreadcrumbs({
    ...(databaseName && databaseName !== 'all' && { database_name: databaseName }),
    ...(schemaName && schemaName !== 'all' && { schema_name: schemaName }),
    ...(tableName && tableName !== 'all' && { table_name: tableName }),
    ...(assetType && { assetType: assetType }),
  })
  const { openMetadataTab } = useDataCatalogNavigation()

  const listDataAssets = useListDataAssetsQuery({
    queryOptions: { enabled: assetType ? assetType === 'database' : false },
  })

  const dataAssets: {
    databases: Database[]
    sensitivity_levels: Record<string, string>
  } = listDataAssets?.data?.data_assets ?? { databases: [], sensitivity_levels: {} }

  // Sync view and filters to URL (only when they actually change)
  useEffect(() => {
    // Skip the initial mount to prevent unnecessary update on load
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    // Check if values have actually changed to prevent unnecessary updates
    const hasChanged =
      tab.tabView !== view ||
      tab.databaseName !== filters.database ||
      tab.schemaName !== filters.schema ||
      tab.tableName !== filters.table ||
      tab.sensitivity !== filters.sensitivity

    if (hasChanged) {
      updateTab({
        tabId: tab.id,
        updates: {
          tabView: view,
          databaseName: filters.database,
          schemaName: filters.schema,
          tableName: filters.table,
          sensitivity: filters.sensitivity,
        },
      })
    }
  }, [
    view,
    filters,
    tab.id,
    tab.tabView,
    tab.databaseName,
    tab.schemaName,
    tab.tableName,
    tab.sensitivity,
    updateTab,
  ])

  // Auto-select first database when data is loaded
  useEffect(() => {
    if (dataAssets.databases.length > 0) {
      const firstDatabase = dataAssets.databases[0]?.database_name
      // Select first database if no database is selected, or if selected database doesn't exist in the list
      const databaseExists =
        filters.database && dataAssets.databases.some((db) => db.database_name === filters.database)

      if (!filters.database || !databaseExists) {
        if (firstDatabase) {
          setFilters((prev) => ({ ...prev, database: firstDatabase }))
        }
      }
    }
  }, [dataAssets.databases, filters.database])

  // Database options
  const databaseOptions = [
    ...dataAssets.databases.map((db) => ({ label: db.database_name, value: db.database_name })),
  ]

  // Schema options filtered by selected database
  const schemaOptions = [
    { label: 'Choose Schema', value: 'all' },
    ...(filters.database
      ? (dataAssets.databases.find((db) => db.database_name === filters.database)?.schemas ?? [])
      : []
    ).map((schema) => ({ label: schema.schema_name, value: schema.schema_name })),
  ]

  // Table options filtered by selected database & schema
  // Only show tables when a specific schema is selected (not 'all')
  const tableOptions = [
    { label: 'Choose Table', value: 'all' },
    ...(filters.database && filters.schema !== 'all'
      ? (dataAssets.databases
          .find((db) => db.database_name === filters.database)
          ?.schemas.find((s) => s.schema_name === filters.schema)?.tables ?? [])
      : []
    ).map((table) => ({ label: table.table_name, value: table.table_name })),
  ]

  // Sensitivity options
  const sensitivityOptions = [
    { label: 'Choose Sensitivity', value: 'all' },
    ...Object.keys(dataAssets.sensitivity_levels).map((key) => ({ label: key, value: key })),
  ]

  // Handler for cascading selection
  const handleSelectChange = (type: FilterKey, value: string) => {
    setFilters((prev) => {
      const updated: FiltersType = { ...prev, [type]: value }

      // Reset dependent filters
      resetMap[type].forEach((key) => {
        updated[key] = 'all'
      })

      // If table is selected (not 'all'), reset sensitivity and disable it
      if (type === 'table' && value !== 'all') {
        updated.sensitivity = 'all'
      }

      return updated
    })
  }

  const updatedBreadcrumbItems = [
    ...breadcrumbItems.map((item) =>
      item.label === tableName
        ? {
            ...item,
            onClick: () => {
              openMetadataTab(databaseName!, schemaName!, tableName!, assetType!)
            },
          }
        : item,
    ),
    { label: 'Data Governance Dashboard' },
  ]

  return (
    <div className='flex w-full flex-col gap-6'>
      {/* Header */}
      <div className='flex flex-col'>
        <PageTemplate.Header hasBreadcrumb className='flex-wrap items-center justify-between gap-4'>
          <div>
            <Breadcrumb items={updatedBreadcrumbItems} />
          </div>
        </PageTemplate.Header>
        <div className='mt-4'>
          <PageTemplate.H4>
            <T keyName='dataGovernanceDashboard.subtitle' />
          </PageTemplate.H4>
        </div>
      </div>

      {/* View Toggle and Filters */}
      <div className='flex flex-wrap items-center justify-between gap-4'>
        <div className='flex w-fit gap-4 rounded-lg bg-gray-100 p-1'>
          <Button
            variant={view === 'metaData' ? 'black' : 'grey'}
            className='flex items-center gap-2 rounded-lg border-none normal-case'
            onClick={() => setView('metaData')}
          >
            <DatabaseIcon />
            <p className='text-sm font-normal'>Metadata View</p>
          </Button>
          <Button
            variant={view === 'dataQuality' ? 'black' : 'grey'}
            className='flex items-center gap-2 rounded-lg border-none normal-case'
            onClick={() => setView('dataQuality')}
          >
            <ChartWithNoAxesIcon className='h-4 w-4' />
            <p className='text-sm font-normal'>Data Quality View</p>
          </Button>
        </div>

        {/* Filters */}
        {assetType && assetType === 'database' && (
          <div className='flex flex-wrap items-center gap-2 rounded-lg border border-gray-200 bg-gray-100 p-2 px-4'>
            <h3 className='min-w-fit text-sm font-medium text-gray-900'>Filters</h3>

            {/* Database */}
            <div className='w-40'>
              {listDataAssets.isLoading ? (
                <Skeleton className='h-10 w-40' />
              ) : (
                <Select
                  value={filters.database}
                  onValueChange={(v) => handleSelectChange('database', v)}
                >
                  <SelectTrigger className='border-input bg-background w-full rounded-md border px-3 py-2 text-xs font-normal'>
                    <div className='w-full overflow-hidden truncate whitespace-nowrap text-left'>
                      {databaseOptions.find((opt) => opt.value === filters.database)?.label ||
                        'Choose a database'}
                    </div>
                  </SelectTrigger>
                  <SelectContent className='max-w-xs'>
                    {databaseOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className='break-all px-2 py-1'>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Schema */}
            <div className='w-40'>
              {listDataAssets.isLoading ? (
                <Skeleton className='h-10 w-40' />
              ) : (
                <Select
                  value={filters.schema}
                  onValueChange={(v) => handleSelectChange('schema', v)}
                >
                  <SelectTrigger className='border-input bg-background w-40 rounded-md border px-3 py-2 text-xs font-normal'>
                    <div className='w-full overflow-hidden truncate whitespace-nowrap text-left'>
                      {schemaOptions.find((opt) => opt.value === filters.schema)?.label ||
                        'Choose a schema'}
                    </div>
                  </SelectTrigger>
                  <SelectContent className='max-w-xs'>
                    {schemaOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className='break-all'>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Table */}
            <div className='w-40'>
              {listDataAssets.isLoading ? (
                <Skeleton className='h-10 w-40' />
              ) : (
                <Select value={filters.table} onValueChange={(v) => handleSelectChange('table', v)}>
                  <SelectTrigger className='border-input bg-background w-40 rounded-md border px-3 py-2 text-xs font-normal'>
                    <div className='w-full overflow-hidden truncate whitespace-nowrap text-left'>
                      {tableOptions.find((opt) => opt.value === filters.table)?.label ||
                        'Choose a table'}
                    </div>
                  </SelectTrigger>
                  <SelectContent className='max-w-xs'>
                    {tableOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className='break-all'>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Sensitivity */}
            <div className='w-40'>
              {listDataAssets.isLoading ? (
                <Skeleton className='h-10 w-40' />
              ) : (
                <Select
                  value={filters.sensitivity}
                  onValueChange={(v) => handleSelectChange('sensitivity', v)}
                  disabled={filters.table !== 'all'} // Disable when table is selected
                >
                  <SelectTrigger className='border-input bg-background w-40 rounded-md border px-3 py-2 text-xs font-normal'>
                    <div className='w-full overflow-hidden truncate whitespace-nowrap text-left'>
                      {sensitivityOptions.find((opt) => opt.value === filters.sensitivity)?.label ||
                        'Choose sensitivity'}
                    </div>
                  </SelectTrigger>
                  <SelectContent className='max-w-xs'>
                    {sensitivityOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value} className='break-all'>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Views */}
      {view === 'metaData' && <MetadataView filters={filters} />}
      {view === 'dataQuality' && <DataQualityView filters={filters} />}
    </div>
  )
}
